import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BarChart3, X } from 'lucide-react';

import ContactForm from '../ContactForm';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ParticlesBackground from '../components/ParticlesBackground';

const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('common.free_diagnostic')}
        </button>
      </div>
    </div>
  </header>
);

const ModelCard = ({ title, description, topLabel, topItems, bottomLabel, bottomItems }) => (
  <article className="bg-white border border-gray-200 rounded-xl p-6 h-full">
    <h3 className="text-xl font-semibold text-[#141414] mb-3">{title}</h3>
    <p className="text-sm text-gray-600 mb-5">{description}</p>

    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm font-semibold text-[#141414] mb-2">{topLabel}</p>
        {topItems.length > 0 && (
          <ul className="space-y-1">
            {topItems.map((item, index) => (
              <li key={`${topLabel}-${index}`} className="text-sm text-gray-700">
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>

      {bottomLabel && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm font-semibold text-[#141414] mb-2">{bottomLabel}</p>
          <ul className="space-y-1">
            {bottomItems.map((item, index) => (
              <li key={`${bottomLabel}-${index}`} className="text-sm text-gray-700">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </article>
);

const ComparisonColumn = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, index) => (
      <li key={index} className="text-sm text-gray-700 leading-relaxed">
        {item}
      </li>
    ))}
  </ul>
);

const OnPremiseVsCloudPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const headers = useMemo(
    () => t('cloud_vs_premise.comparison.headers', { returnObjects: true }),
    [t]
  );

  const advisoryItems = useMemo(
    () => t('cloud_vs_premise.advisory.items', { returnObjects: true }),
    [t]
  );

  const comparisonRows = useMemo(
    () => [
      {
        label: t('cloud_vs_premise.comparison.rows.control.label'),
        saas: t('cloud_vs_premise.comparison.rows.control.saas', { returnObjects: true }),
        selfHosted: t('cloud_vs_premise.comparison.rows.control.self_hosted', { returnObjects: true }),
        inHouse: t('cloud_vs_premise.comparison.rows.control.in_house', { returnObjects: true }),
      },
      {
        label: t('cloud_vs_premise.comparison.rows.scaling.label'),
        saas: t('cloud_vs_premise.comparison.rows.scaling.saas', { returnObjects: true }),
        selfHosted: t('cloud_vs_premise.comparison.rows.scaling.self_hosted', { returnObjects: true }),
        inHouse: t('cloud_vs_premise.comparison.rows.scaling.in_house', { returnObjects: true }),
      },
      {
        label: t('cloud_vs_premise.comparison.rows.costs.label'),
        saas: t('cloud_vs_premise.comparison.rows.costs.saas', { returnObjects: true }),
        selfHosted: t('cloud_vs_premise.comparison.rows.costs.self_hosted', { returnObjects: true }),
        inHouse: t('cloud_vs_premise.comparison.rows.costs.in_house', { returnObjects: true }),
      },
      {
        label: t('cloud_vs_premise.comparison.rows.maintenance.label'),
        saas: t('cloud_vs_premise.comparison.rows.maintenance.saas', { returnObjects: true }),
        selfHosted: t('cloud_vs_premise.comparison.rows.maintenance.self_hosted', { returnObjects: true }),
        inHouse: t('cloud_vs_premise.comparison.rows.maintenance.in_house', { returnObjects: true }),
      },
    ],
    [t]
  );

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <ParticlesBackground />

      {isPopupOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup();
          }}
        >
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('common.close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('common.diagnostic_request')}</h3>
              <p className="text-gray-600 mb-6">{t('common.diagnostic_desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} t={t} />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <section className="text-center mb-20">
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('cloud_vs_premise.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-5">
              {t('cloud_vs_premise.hero.subtitle')}
            </p>
            <p className="text-base text-gray-500 max-w-4xl mx-auto">
              {t('cloud_vs_premise.hero.desc1')}
              <br />
              {t('cloud_vs_premise.hero.desc2')}
            </p>
          </section>

          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">
              {t('cloud_vs_premise.models.title')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ModelCard
                title={t('cloud_vs_premise.models.saas.title')}
                description={t('cloud_vs_premise.models.saas.desc')}
                topLabel={t('cloud_vs_premise.models.saas.user')}
                topItems={[]}
                bottomLabel={t('cloud_vs_premise.models.saas.provider')}
                bottomItems={t('cloud_vs_premise.models.saas.points', { returnObjects: true })}
              />

              <ModelCard
                title={t('cloud_vs_premise.models.self_hosted.title')}
                description={t('cloud_vs_premise.models.self_hosted.desc')}
                topLabel={t('cloud_vs_premise.models.self_hosted.user')}
                topItems={t('cloud_vs_premise.models.self_hosted.points_user', { returnObjects: true })}
                bottomLabel={t('cloud_vs_premise.models.self_hosted.provider')}
                bottomItems={t('cloud_vs_premise.models.self_hosted.points_provider', { returnObjects: true })}
              />

              <ModelCard
                title={t('cloud_vs_premise.models.in_house.title')}
                description={t('cloud_vs_premise.models.in_house.desc')}
                topLabel={t('cloud_vs_premise.models.in_house.user')}
                topItems={t('cloud_vs_premise.models.in_house.points', { returnObjects: true })}
              />
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">
              {t('cloud_vs_premise.comparison.title')}
            </h2>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="hidden md:grid md:grid-cols-4 gap-4 p-4 bg-gray-50 border-b border-gray-200">
                <div className="font-semibold text-gray-800">{headers[0]}</div>
                <div className="font-semibold text-gray-800">{headers[1]}</div>
                <div className="font-semibold text-gray-800">{headers[2]}</div>
                <div className="font-semibold text-gray-800">{headers[3]}</div>
              </div>

              {comparisonRows.map((row, index) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-1 md:grid-cols-4 gap-5 p-5 ${
                    index < comparisonRows.length - 1 ? 'border-b border-gray-200' : ''
                  }`}
                >
                  <div>
                    <p className="font-semibold text-[#141414] md:hidden mb-2">{headers[0]}</p>
                    <p className="font-semibold text-[#141414]">{row.label}</p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-700 md:hidden mb-2">{headers[1]}</p>
                    <ComparisonColumn items={row.saas} />
                  </div>

                  <div>
                    <p className="font-semibold text-gray-700 md:hidden mb-2">{headers[2]}</p>
                    <ComparisonColumn items={row.selfHosted} />
                  </div>

                  <div>
                    <p className="font-semibold text-gray-700 md:hidden mb-2">{headers[3]}</p>
                    <ComparisonColumn items={row.inHouse} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">
              {t('cloud_vs_premise.advisory.title')}
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12">
              {t('cloud_vs_premise.advisory.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {advisoryItems.map((item, index) => (
                <article
                  key={item.title}
                  className="bg-white border border-gray-200 rounded-xl p-6 h-full"
                >
                  <p className="text-sm font-semibold text-gray-500 mb-2">{index + 1}. </p>
                  <h3 className="text-lg font-semibold text-[#141414] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{item.desc}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        <section className="max-w-4xl mx-auto mt-24 mb-12">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-[#141414] mb-4">
              {t('cloud_vs_premise.final.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              {t('cloud_vs_premise.final.desc')}
            </p>
            <button
              onClick={openPopup}
              className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition font-semibold"
            >
              <BarChart3 size={18} />
              {t('cloud_vs_premise.final.btn')}
            </button>
            <p className="text-sm text-gray-500 max-w-xl mx-auto mt-4">
              ({t('cloud_vs_premise.final.note')})
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OnPremiseVsCloudPage;

